import React, { useCallback, useEffect, useState } from "react";
import { X, UserPlus } from "lucide-react";
import toast from "react-hot-toast";
import { getAgents, patchProperty } from "../../../api/agent";
import InfiniteScrollSelect from "../../../components/common/InfiniteScrollSelect";

/**
 * Change the agent on an appraisal or listing from the Prospects list.
 *
 * Christian Gravias, 21 Oct 2026 (CRM-2026-00113): "in each of these tabs where
 * it says assigned please make it a button where we can change the agents".
 * Leads go through AssignLeadModal (email + hand-over note); a property row has
 * no lead behind it to email about, so this just moves the primary agent, the
 * same field the Edit Property form saves.
 */
const AssignPropertyAgentModal = ({ isOpen, property, label = "", agents = [], onClose, onAssigned }) => {
  const [agentId, setAgentId] = useState("");
  const [saving, setSaving] = useState(false);

  const currentAgentId = property?.agent && typeof property.agent === "object" ? property.agent.id : property?.agent;

  useEffect(() => {
    if (!isOpen) return;
    setAgentId(currentAgentId ? String(currentAgentId) : "");
  }, [isOpen, currentAgentId, property?.id]);

  const fetchAgentOptions = useCallback(async (page, pageSize, search = "") => {
    const res = await getAgents({ page, page_size: pageSize, search, role: "agent" });
    const data = res?.data ?? {};
    return {
      results: data.results ?? (Array.isArray(data) ? data : []),
      next: Boolean(data.next),
    };
  }, []);

  if (!isOpen || !property) return null;

  const address = label || "This property";
  const isSameAgent = !!agentId && agentId === String(currentAgentId || "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (saving || !agentId || isSameAgent) return;
    setSaving(true);
    try {
      await patchProperty(property.id, { agent: agentId });
      const name = agents.find((a) => String(a.id) === agentId)?.name;
      toast.success(`Agent changed${name ? ` to ${name}` : ""}.`);
      onAssigned && onAssigned();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to change the agent.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center bg-black/40 p-3 overflow-y-auto">
      <form onSubmit={handleSubmit} role="dialog" aria-label="Change Agent" className="bg-white rounded-xl shadow-xl w-full max-w-md my-6">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-bold text-[#2D3441] flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-[#55C5D0]" />
            Change Agent
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close change agent">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div className="rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-sm font-bold text-gray-900">{address}</div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Agent</label>
            <InfiniteScrollSelect
              value={agentId}
              onChange={(val) => setAgentId(val ? String(val) : "")}
              placeholder="Pick an agent"
              fetchOptions={fetchAgentOptions}
              initialOptions={agents}
              getLabel={(a) => a.name || a.email}
              getValue={(a) => a.id}
            />
            {isSameAgent && <p className="mt-1 text-[11px] text-gray-400">This is already the agent on this property.</p>}
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-gray-600 hover:text-gray-800">
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !agentId || isSameAgent}
            className="bg-[#55C5D0] hover:bg-[#45b5c0] disabled:opacity-60 text-white px-4 py-2 rounded-lg font-bold text-sm"
          >
            {saving ? "Saving..." : "Change Agent"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssignPropertyAgentModal;
